import { OverlayRef } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  effect,
  forwardRef,
  inject,
  Injector,
  input,
  OnDestroy,
  OnInit,
  output,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgControl,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatRadioModule } from '@angular/material/radio';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DocumentTagLabelPipe } from '@app/features/documents/document-tag-label-pipe';
import {
  acceptedDocumentsMimeTypes,
  CdTDocument,
  DocumentTag,
} from '@app/features/documents/document.model';
import { DropInputFile } from '@app/features/documents/drop-input-file/drop-input-file';
import { LayoutService } from '@app/nav/layout/layout.service';
import { ControlsOf } from '@app/shared/forms/controls-of.form-group';
import { DocumentService } from '../document-service';

export type DocumentFormGroup = FormGroup<ControlsOf<CdTDocument[]>>;
export type DocumentState = 'provided' | 'not-applicable' | 'waiting' | 'error';

@Component({
  selector: 'app-document-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DocumentTagLabelPipe,
    MatCheckboxModule,
    MatRadioModule,
    DropInputFile,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
  ],
  templateUrl: './document-form.html',
  styleUrls: ['./document-form.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DocumentForm),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => DocumentForm),
      multi: true,
    },
  ],
})
export class DocumentForm implements OnInit, OnDestroy, ControlValueAccessor, Validator {
  acceptedImageMimeTypes = acceptedDocumentsMimeTypes;

  tag = input.required<DocumentTag>();
  readOnly = input<boolean>(false);
  required = input<boolean>(false);
  uploaded = output<CdTDocument>();

  isValid = true;
  value: CdTDocument[] = [];
  private overlayRef?: OverlayRef;
  isDisabled = false;

  get isReadOnly(): boolean {
    return this.isDisabled || this.readOnly();
  }

  documentService = inject(DocumentService);
  layoutService = inject(LayoutService);
  private cdr = inject(ChangeDetectorRef);
  private injector = inject(Injector);
  private ngControl: NgControl | null = null;
  private onValidatorChange: (() => void) | null = null;

  constructor() {
    effect(() => {
      // Recompute validator state whenever required input toggles.
      this.required();
      this.onValidatorChange?.();
      this.ngControl?.control?.updateValueAndValidity({ emitEvent: false });
    });
  }

  preview(document: CdTDocument) {
    this.layoutService.previewDocument(document);
  }

  ngOnInit(): void {
    this.ngControl = this.injector.get(NgControl, null);
  }

  onChange: (value: CdTDocument[]) => void = () => {
    // No-op
  };
  onTouched: () => void = () => {
    // No-op
  };

  writeValue(value: CdTDocument[] | undefined): void {
    this.value = value || [];
  }

  registerOnChange(fn: (value: CdTDocument[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    const valid = control.disabled || !this.required() || this.value.length > 0;
    this.isValid = valid;
    return valid ? null : { required: true };
  }

  registerOnValidatorChange?(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  onChoosenFile(files: File[]): void {
    if (this.isReadOnly) {
      return;
    }

    for (const oneFile of files) {
      const oneDocument: CdTDocument = {
        id: '',
        tag: this.tag(),
        name: oneFile.name,
        mimeType: oneFile.type,
        createdAt: new Date().toISOString(),
        size: oneFile.size,
      };
      // mark as waiting for upload
      oneDocument.uploadState = 'waiting';
      oneDocument.uploadError = undefined;

      this.value = [...this.value, oneDocument];
      this.cdr.detectChanges();

      this.documentService.createDocument(oneFile).subscribe({
        next: (response) => {
          console.log('Upload successful:', response);
          // Le document créé fait foi : l'API a pu convertir le fichier (HEIC en JPEG).
          oneDocument.id = response.id;
          oneDocument.name = response.name;
          oneDocument.mimeType = response.mimeType;
          oneDocument.size = response.size;
          oneDocument.createdAt = response.createdAt;
          oneDocument.createdBy = response.createdBy;
          if (response.orientations) {
            oneDocument.orientations = response.orientations;
          }
          oneDocument.uploadState = 'provided';
          oneDocument.uploadError = undefined;
          this.onChange(this.value);

          this.ngControl?.control?.updateValueAndValidity({ emitEvent: true });
          this.cdr.detectChanges();
          this.uploaded.emit(oneDocument);
        },
        error: (error) => {
          console.error('Upload failed:', error);
          oneDocument.uploadState = 'error';
          oneDocument.uploadError = error?.message ?? String(error);
          // keep the document in the list so user can see the error
          this.cdr.detectChanges();
        },
      });
    }
  }

  removeFile(document: CdTDocument): void {
    if (this.isReadOnly) {
      return;
    }

    if (confirm('Le document "' + document.name + '" sera supprimé. Êtes-vous sûr ?')) {
      console.log('Request to remove document:', document);
      this.documentService.deleteDocument(document.id).subscribe({
        next: () => {
          console.log('Document deleted successfully');
          this.filterOut(document);
        },
        error: (error) => {
          console.error('Failed to delete document:', error);
        },
      });
    }
  }

  filterOut(document: CdTDocument) {
    if (this.isReadOnly) {
      return;
    }

    this.value = this.value.filter((d) => d.id !== document.id);
    this.onChange(this.value);
    this.ngControl?.control?.updateValueAndValidity({ emitEvent: true });
  }

  ngOnDestroy(): void {
    this.overlayRef?.dispose();
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }
}
